import React from 'react'
import { Link } from '@inertiajs/react'
import { cn } from '@/lib/utils'
import { HelpTooltip } from '@/components/HelpTooltip'

const TABS = [
  { value: 'rooms-setup', label: 'Rooms Setup' },
  { value: 'seasons', label: 'Seasons' },
  { value: 'day-of-week', label: 'Day of Week' },
  { value: 'demand-response', label: 'Demand Response' },
  { value: 'events', label: 'Events' },
  { value: 'gap-filling', label: 'Gap Filling' },
  // The FEATURE's name, on a tab: operators know "last-minute discount", and clearance is the word
  // that replaces "discount" — dropping "Last-Minute" left the tab naming a warehouse sale.
  { value: 'clearance', label: 'Last-Minute Clearance' },
  { value: 'price-guardrails', label: 'Price Guardrails' },
  { value: 'market-position', label: 'Market Position' },
]

interface PricingRulesNavProps {
  /** The property's backend-served canonical path. */
  propertyBasePath: string
  activeTab: string
  /** What this tab does, for the heading's HelpTooltip. */
  help: { content: string; learnMore?: string }
}

/**
 * THE HEAD OF EVERY PRICING-RULES TAB: the heading, its help and the tabs, shared so the nine tabs
 * cannot drift apart. It names no hotel (owner ruling 2026-10-26): the top bar names the hotel and the
 * business on every page, and the review before every save names both again.
 */
export default function PricingRulesNav({ propertyBasePath, activeTab, help }: PricingRulesNavProps) {
  const base = propertyBasePath

  return (
    <>
      <div className="animate-slide-in" style={{ animationDelay: '0ms' }}>
        <div className="flex items-center gap-2">
          <h1 className="text-xl font-bold tracking-tight text-foreground">Pricing Rules</h1>
          <HelpTooltip content={help.content} learnMore={help.learnMore} />
        </div>
      </div>

      {/* Nine tabs outgrow a narrow window, so the strip scrolls sideways instead of the page doing it. */}
      <div className="animate-slide-in max-w-full overflow-x-auto" style={{ animationDelay: '50ms' }}>
        <nav className="inline-flex h-10 items-center rounded-md bg-muted p-1 text-muted-foreground">
          {TABS.map((tab) => (
            <Link
              key={tab.value}
              href={`${base}/pricing-rules/${tab.value}`}
              className={cn(
                'inline-flex items-center justify-center whitespace-nowrap rounded-sm px-3 py-1.5 text-sm font-medium transition-all',
                activeTab === tab.value
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground'
              )}
            >
              {tab.label}
            </Link>
          ))}
        </nav>
      </div>
    </>
  )
}
